import React from 'react';
import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import EditIcon from '@atlaskit/icon/core/edit';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import ListChecklistIcon from '@atlaskit/icon/core/list-checklist';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import PersonIcon from '@atlaskit/icon/core/person';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import TargetIcon from '@atlaskit/icon/core/target';
import TaskIcon from '@atlaskit/icon/core/task';

import { HandleActionType } from '@atlassian/rovo-conversation-actions';

/**
 * Internationalized messages for create quick action titles
 */
const i18n = defineMessages({
	// Product Management
	defineRequirements: {
		id: 'experiment-rovo-chat-create-quick-actions.defineRequirements',
		defaultMessage: 'Define product requirements',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create product requirements.',
	},
	identifyStakeholders: {
		id: 'experiment-rovo-chat-create-quick-actions.identifyStakeholders',
		defaultMessage: 'Identify key partners',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a stakeholder map.',
	},
	gatherInsights: {
		id: 'experiment-rovo-chat-create-quick-actions.gatherInsights',
		defaultMessage: 'Gather insights',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create user insights.',
	},
	// Engineering
	sketchPlan: {
		id: 'experiment-rovo-chat-create-quick-actions.sketchPlan',
		defaultMessage: 'Sketch out a plan',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a sprint planning whiteboard.',
	},
	documentDecisions: {
		id: 'experiment-rovo-chat-create-quick-actions.documentDecisions',
		defaultMessage: 'Document key decisions',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a DACI decision document.',
	},
	draftApiDocs: {
		id: 'experiment-rovo-chat-create-quick-actions.draftApiDocs',
		defaultMessage: 'Draft API guidance',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create API documentation.',
	},
	// Marketing
	gtmPlan: {
		id: 'experiment-rovo-chat-create-quick-actions.gtmPlan',
		defaultMessage: 'Map a path to market',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a go-to-market plan.',
	},
	competitorAnalysis: {
		id: 'experiment-rovo-chat-create-quick-actions.competitorAnalysis',
		defaultMessage: 'Compare to top competitors',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a competitor analysis.',
	},
	contentIdeas: {
		id: 'experiment-rovo-chat-create-quick-actions.contentIdeas',
		defaultMessage: 'Build up brand visibility',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create content ideas.',
	},
	// Sales
	discoveryQuestions: {
		id: 'experiment-rovo-chat-create-quick-actions.discoveryQuestions',
		defaultMessage: 'Start the conversation',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create sales discovery questions.',
	},
	salesTimeline: {
		id: 'experiment-rovo-chat-create-quick-actions.salesTimeline',
		defaultMessage: 'Visualize the journey',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a sales timeline.',
	},
	introEmail: {
		id: 'experiment-rovo-chat-create-quick-actions.introEmail',
		defaultMessage: 'Connect with key deciders',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create an introduction email.',
	},
	// Program Management
	projectPlan: {
		id: 'experiment-rovo-chat-create-quick-actions.projectPlan',
		defaultMessage: 'Build a phased structure',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a project plan.',
	},
	riskAssessment: {
		id: 'experiment-rovo-chat-create-quick-actions.riskAssessment',
		defaultMessage: 'De-risk the program',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a risk assessment.',
	},
	statusReport: {
		id: 'experiment-rovo-chat-create-quick-actions.statusReport',
		defaultMessage: 'Reflect on past work',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a retrospective.',
	},
	// Other/General
	analyzeFeedback: {
		id: 'experiment-rovo-chat-create-quick-actions.analyzeFeedback',
		defaultMessage: 'Surface any blockers',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to summarize key risks.',
	},
	brainstormIdeas: {
		id: 'experiment-rovo-chat-create-quick-actions.brainstormIdeas',
		defaultMessage: 'Reflect on past work',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a team retrospective.',
	},
	researchPlan: {
		id: 'experiment-rovo-chat-create-quick-actions.researchPlan',
		defaultMessage: 'Catalog critical assets',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a content library.',
	},
});

/**
 * Job title categories for prompt targeting
 */
export type JobTitleCategory =
	| 'engineering'
	| 'productManagement'
	| 'marketing'
	| 'sales'
	| 'programManagement'
	| 'other';

/**
 * Configuration for a job title category-based prompt
 */
export type JobTitleBasedPrompt = {
	id: string;
	title: MessageDescriptor;
	prompt: string;
	userJobTitleCategory: JobTitleCategory;
	icon: React.ComponentType<{ label: string }>;
};

/**
 * Child action generated from a job title category-based prompt
 * Used by the conversation assistant UI
 */
export type JobTitleBasedChildAction = {
	id: string;
	title: string;
	icon: () => React.ReactElement;
	handleType: HandleActionType;
	dynamicPrompt: string;
};

/**
 * Create related quick actions prompts configuration
 * Organized by job title category with top 3 prompts per category
 */
export const CREATE_QUICK_ACTIONS_PROMPTS: JobTitleBasedPrompt[] = [
	// Product Management
	{
		id: 'define-requirements',
		title: i18n.defineRequirements,
		prompt:
			'Create a Confluence page with a PRD for [project]. Include sections for user stories, with acceptance criteria for each story, success metrics (KPIs) for the feature, and technical considerations',
		userJobTitleCategory: 'productManagement',
		icon: ListChecklistIcon,
	},
	{
		id: 'identify-stakeholders',
		title: i18n.identifyStakeholders,
		prompt:
			'Create a Confluence whiteboard with a stakeholder map for [feature or project], including internal teams (engineering, product, customer success), external partners, and customer segments with influence/interest ratings',
		userJobTitleCategory: 'productManagement',
		icon: PeopleGroupIcon,
	},
	{
		id: 'gather-insights',
		title: i18n.gatherInsights,
		prompt:
			'Create a Confluence page with competitive research on [company 1, company 2, company 3]. Focus on recent feature launches, pricing, and compare positioning to our own',
		userJobTitleCategory: 'productManagement',
		icon: LightbulbIcon,
	},

	// Engineering
	{
		id: 'sketch-plan',
		title: i18n.sketchPlan,
		prompt:
			'Create a Confluence whiteboard for sprint planning for the upcoming release cycle of [feature or project], listing team members, sprint goals, prioritized backlog items, and story point estimates',
		userJobTitleCategory: 'engineering',
		icon: RoadmapIcon,
	},
	{
		id: 'document-decisions',
		title: i18n.documentDecisions,
		prompt:
			'Create a Confluence page with an architecture DACI for [recent decision] for [feature or project]',
		userJobTitleCategory: 'engineering',
		icon: EditIcon,
	},
	{
		id: 'draft-api-docs',
		title: i18n.draftApiDocs,
		prompt:
			'Create a Confluence page with API documentation for [service]. Include details for request parameters, response body (JSON schema), error codes (404, 500), and a cURL example',
		userJobTitleCategory: 'engineering',
		icon: ListChecklistIcon,
	},

	// Marketing
	{
		id: 'gtm-plan',
		title: i18n.gtmPlan,
		prompt:
			'Create a Confluence page with a GTM launch plan for [feature or project] targeting [audience segment], including key goals, channels, and metrics',
		userJobTitleCategory: 'marketing',
		icon: RoadmapIcon,
	},
	{
		id: 'competitor-analysis',
		title: i18n.competitorAnalysis,
		prompt:
			'Create a Confluence whiteboard with a market analysis map comparing our [feature or solution] to [competitors]',
		userJobTitleCategory: 'marketing',
		icon: ChartTrendUpIcon,
	},
	{
		id: 'content-ideas',
		title: i18n.contentIdeas,
		prompt:
			'Create a Confluence page to brainstorm 20 ideas for content targeting [audience] for the awareness funnel stage of [feature or project]. For each, include format, key message, and suggested channel',
		userJobTitleCategory: 'marketing',
		icon: MegaphoneIcon,
	},

	// Sales
	{
		id: 'discovery-questions',
		title: i18n.discoveryQuestions,
		prompt:
			'Create a Confluence page with 10 discovery questions for a first call with a [buyer persona] at a [industry] company.',
		userJobTitleCategory: 'sales',
		icon: PersonIcon,
	},
	{
		id: 'sales-timeline',
		title: i18n.salesTimeline,
		prompt:
			'Create a Confluence whiteboard to diagram a timeline of the sales process for [customer], marking all major touchpoints and interactions',
		userJobTitleCategory: 'sales',
		icon: CalendarIcon,
	},
	{
		id: 'intro-email',
		title: i18n.introEmail,
		prompt:
			'Create a Confluence page with an email to a CTO introducing [feature or solution] as a solution, focusing on its value proposition for someone responsible for [area].',
		userJobTitleCategory: 'sales',
		icon: TargetIcon,
	},

	// Program Management
	{
		id: 'project-plan',
		title: i18n.projectPlan,
		prompt:
			'Create a Confluence page with a project plan highlighting various product management phases, including design, build, test, and release for [program].',
		userJobTitleCategory: 'programManagement',
		icon: TaskIcon,
	},
	{
		id: 'risk-assessment',
		title: i18n.riskAssessment,
		prompt:
			'Create a Confluence page with a risk management checklist for the [program] initiative, identifying potential blockers and providing mitigation strategies',
		userJobTitleCategory: 'programManagement',
		icon: ChartTrendUpIcon,
	},
	{
		id: 'status-report',
		title: i18n.statusReport,
		prompt:
			'Create a Confluence whiteboard with a retro for the [project or feature] launch with sections for what went well, what did not go as planned, and any key takeaways or action items',
		userJobTitleCategory: 'programManagement',
		icon: ListChecklistIcon,
	},

	// Other/General
	{
		id: 'analyze-feedback',
		title: i18n.analyzeFeedback,
		prompt:
			'Create a Confluence page to summarize key risks and open questions from [feature or project]',
		userJobTitleCategory: 'other',
		icon: ChartTrendUpIcon,
	},
	{
		id: 'brainstorm-ideas',
		title: i18n.brainstormIdeas,
		prompt:
			'Create a Confluence whiteboard with a retro board for the upcoming session with my team',
		userJobTitleCategory: 'other',
		icon: LightbulbIcon,
	},
	{
		id: 'research-plan',
		title: i18n.researchPlan,
		prompt:
			'Create a Confluence page to set up a content library database for [project or launch] to organize marketing assets and blog posts with fields for asset type, target audience, status, and tags',
		userJobTitleCategory: 'other',
		icon: ListChecklistIcon,
	},
];

/**
 * Filters create quick action prompts by user job title category
 */
export function getPromptsForJobTitleCategory(
	userJobTitleCategory: JobTitleCategory,
): JobTitleBasedPrompt[] {
	return CREATE_QUICK_ACTIONS_PROMPTS.filter(
		(prompt) => prompt.userJobTitleCategory === userJobTitleCategory,
	);
}

/**
 * Converts create quick action prompts to child actions format
 */
export function getCreateQuickActionsChildren(
	prompts: JobTitleBasedPrompt[],
	intl: IntlShape,
): JobTitleBasedChildAction[] {
	return prompts.map((prompt) => ({
		id: `${prompt.id}-rovo-quick-action`,
		title: intl.formatMessage(prompt.title),
		icon: () => <prompt.icon label="" />,
		handleType: HandleActionType.Chat,
		dynamicPrompt: prompt.prompt,
	}));
}

/**
 * Keyword patterns for each job title category
 */
const JOB_TITLE_KEYWORDS: Record<Exclude<JobTitleCategory, 'other'>, string[]> = {
	engineering: [
		'engineer',
		'developer',
		'architect',
		'sre',
		'devops',
		'software',
		'technical lead',
		'tech lead',
	],
	productManagement: ['product manager', 'product owner', 'product lead', 'pm '],
	marketing: ['marketing', 'brand', 'content', 'communications', 'growth'],
	sales: ['sales', 'account executive', 'account manager', 'business development', 'bdr', 'sdr'],
	programManagement: [
		'program manager',
		'project manager',
		'delivery manager',
		'scrum master',
		'agile coach',
		'pmo',
	],
};

/**
 * Maps a job title string to a JobTitleCategory
 * Uses keyword matching to determine the category
 */
export function mapJobTitleToCategory(jobTitle: string | null): JobTitleCategory {
	if (!jobTitle) {
		return 'other';
	}

	const normalizedTitle = jobTitle.toLowerCase();

	// Safe to iterate: fixed set of 5 categories with ~30 total keywords.
	// Not user-controlled data, so no risk of quadratic performance issues.
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
	for (const [category, keywords] of Object.entries(JOB_TITLE_KEYWORDS)) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		if (keywords.some((keyword) => normalizedTitle.includes(keyword))) {
			return category as JobTitleCategory;
		}
	}

	return 'other';
}

/**
 * Wrapper that maps a job title to quick action children.
 */
export function getChildrenActionsForJobTitle(
	jobTitle: string | null,
	intl: IntlShape,
): JobTitleBasedChildAction[] {
	const category = mapJobTitleToCategory(jobTitle);
	const prompts = getPromptsForJobTitleCategory(category);
	return getCreateQuickActionsChildren(prompts, intl);
}
